import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {SectionBase} from '../../../components/section/section-base';
import {
  ContactDataStateService
} from '../../../../core/services/impl/section-services/contact-data/contact-data.service';
import {AgreementModel} from '../../../../core/models/agreement.model';
import {LanguageService} from '../../../services/language.service';
import {ConfigurationService} from '../../../../core/services/impl/configuration/configuration.service';
import {isNullOrUndefined} from 'util';
import {ControlChange} from '../../../../core/models/control-change.model';
import {
  PredefinedValuesStateService
} from '../../../../core/services/impl/section-services/predefined-values/predefined-values-state.service';
import {
  PredefinedValuesService
} from '../../../../core/services/impl/section-services/predefined-values/predefined-values.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {AgreementsService} from '../../../services/agreements.service';
import {ContactDataSectionConfig} from './contact-data-section.config';
import {ContactDataConsts} from '../../../../core/consts/contact-data.consts';
import {AssetsService} from '../../../services/assets.service';
import {Validators} from '@angular/forms';
import {PhoneNumberZeroesValidator} from '../../../validators/zeroes.validator';
import {UrlParamsService} from '../../../../core/services/impl/url-params.service';
import {isCarOne, isNext} from '../../../../core/utils/utils';
import {
  CheckboxesGroupComponent,
  CheckboxesGroupConfig
} from '../../../components/checkboxes-group/checkboxes-group.component';
import {ApplicationContextService} from '../../../../core/services/impl/configuration/application-context.service';
import {PhoneNumberRequiredValidator} from '../../../validators/phone-number-required.validator';
import {StepKey} from '../../../../core/enums/step-key.enum';
import {Subscription} from 'rxjs/Rx';
import {FieldsVisibilityService} from '../../../../core/services/fields-visibility.service';
import {OwnerDataConsts} from '../../../../core/consts/owner-data.consts';
import {RegExpKey, RegExpService} from '../../../services/reg-exp.service';

@Component({
  selector: 'app-contact-data-section',
  templateUrl: './contact-data-section.component.html',
  styleUrls: ['./contact-data-section.component.scss']
})
export class ContactDataSectionComponent extends SectionBase implements OnInit, OnDestroy {

  @ViewChild('checkboxesGroup') checkboxesGroup: CheckboxesGroupComponent;
  public config: ContactDataSectionConfig = new ContactDataSectionConfig(this.predefined, this.agreementsService, this.contextService, this.urlParamsService, this.regExpService);
  public consts = ContactDataConsts;
  isNext = isNext();
  agreements: any[];
  readonlyAgreements: AgreementModel[];
  checkboxesGroupConfig: CheckboxesGroupConfig;
  checkboxesGroupFieldKeys: string[];
  requiredFieldInfoVisible: boolean;
  private iconsDirectoryPath: string;
  private subscriptions: Subscription[] = [];

  get agreementsVisible(): boolean {
    return this.checkboxesGroupConfig && this.agreements && this.agreements.length > 0;
  }

  get language(): string {
    return this.languageService.getDefaultLanguage().toUpperCase();
  }

  get phoneNumberHintVisible(): boolean {
    return !isCarOne() && !this.contextService.isDiagnosticStationContext;
  }

  get phoneNumberVisible(): boolean {
    return this.contextService.calculationPhoneNumberVisible && this.isFieldVisible(OwnerDataConsts.PHONE_NUMBER);
  }

  get readonlyAgreementsAllowed(): boolean {
    return !this.contextService.isDiagnosticStationContext;
  }

  get agreementsRequiredSymbol(): string {
    return this.agreementsService.REQUIRED_SYMBOL;
  }

  constructor(public stateService: ContactDataStateService,
              private assetsService: AssetsService,
              private languageService: LanguageService,
              private predefined: PredefinedValuesService,
              private predefinedState: PredefinedValuesStateService,
              private agreementsService: AgreementsService,
              private urlParamsService: UrlParamsService,
              private contextService: ApplicationContextService,
              private configuration: ConfigurationService,
              private fieldsVisibilityService: FieldsVisibilityService,
              private regExpService: RegExpService) {
    super();
    this.iconsDirectoryPath = this.assetsService.getIconsDirectoryPath();
  }

  ngOnInit() {
    if (this.agreementsService.agreementsCollectible) {
      this.mockAgreements();
      this.initAgreements();
      this.initCheckboxesGroup();
    }
    this.initBean();
    this.initIsFieldDirtyBean();
    this.createConfig();
    this.buildForm();
    this.updatePhoneNumberValidators();

    this.initControlsChanges();
    this.handleAgreementsUpdateFromDifferentSection();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }

  mockAgreements(): void {
    if (isNotNullOrUndefined(this.configuration.uiConfig.agreementsConfig)) {
      const agreements = this.agreementsService.getAgreementsForDestination(StepKey.FIRST_STEP_3);
      this.agreements = agreements.filter(agreement => !agreement.details.readonly);
      this.readonlyAgreements = agreements.filter(agreement => agreement.details.readonly);
    } else {
      this.agreements = [];
      this.readonlyAgreements = [];
    }
    this.requiredFieldInfoVisible = this.agreements.some((agreement: AgreementModel) => agreement.details.required);
  }

  initAgreements(): void {
    if (isNullOrUndefined(this.stateService.agreements) || this.stateService.agreements.length === 0) {
      this.stateService.agreements = this.agreements;
    }
  }

  initBean() {
    this.bean = this.stateService.contactDataBean;
    this.bean.agreements = this.bean.agreements.filter(agreement => this.agreements.find(item => item.code === agreement.code));
  }

  createConfig() {
    this.formConfig = this.config.createConfig(this.languageService.getDefaultLanguage());
    if (this.agreementsService.agreementsCollectible) {
      this.agreements.forEach(item => {
        this.formConfig.fieldsConfig.push(this.config.createAgreementConfig(item));
      });
    }
  }

  initControlsChanges() {
    let agreementsControlChanges: ControlChange[] = [];
    if (this.agreementsService.agreementsCollectible) {
      this.agreements.forEach(item => {
        agreementsControlChanges.push({controlName: item.code, behaviour: value => this.stateService.changeAgreement(item.code, value)})
      });
    }

    const controlsChanges: ControlChange[] = [
      {controlName: this.consts.EMAIL, behaviour: value => this.stateService.changeEmail(value)}
    ];

    if (this.phoneNumberVisible) {
      controlsChanges.push({
        controlName: this.consts.PHONE_NUMBER,
        behaviour: value => {
          this.updatePhoneNumberValidators();
          this.stateService.changePhoneNumber(value);
        }
      });
    }
    this.handleControlsState(
        ...controlsChanges,
        ...agreementsControlChanges);
  }

  markAsDirty(event) {
    this.predefinedState.isDirtyBean = event;
  }

  getAgreementValue(agreement: AgreementModel): string {
    const agreementValues = agreement.details.values.filter(item => item.languageCode.toUpperCase() === this.language);
    if (agreementValues.length > 0) {
      return agreementValues[0].value;
    } else {
      throw new Error(`Agreement does not contain value for given language code: ${this.language}.`);
    }
  }

  isFieldVisible(fieldKey: string): boolean {
    return this.canFieldDisplayed(fieldKey) && this.fieldsVisibilityService.isFieldVisible('CONTACT_SECTION', fieldKey);
  }

  private initIsFieldDirtyBean() {
    this.isDirtyBean = this.predefinedState.isDirtyBean;
  }

  private updatePhoneNumberValidators(): void {
    if (this.phoneNumberVisible) {
      const phoneNumber = this.getControl(this.consts.PHONE_NUMBER).value;
      if (phoneNumber && phoneNumber.length > 2) {
        this.getControl(this.consts.PHONE_NUMBER).setValidators([
          PhoneNumberZeroesValidator,
          Validators.pattern(this.regExpService.getRegExp(RegExpKey.PHONE_NUMBER)),
          Validators.minLength(2),
          Validators.maxLength(11)
        ])
      } else {
        this.getControl(this.consts.PHONE_NUMBER).clearValidators();
        if (this.contextService.calculationPhoneNumberVisible && this.contextService.calculationPhoneNumberRequired) {
          this.getControl(this.consts.PHONE_NUMBER).setValidators([
            PhoneNumberRequiredValidator
          ]);
        }
      }
      this.getControl(this.consts.PHONE_NUMBER).updateValueAndValidity({emitEvent: false});
    }
  }

  private initCheckboxesGroup(): void {
    this.checkboxesGroupConfig = {
      checkAllLabel: 'SHARED.AGREEMENTS.checkAllAgreements'
    };
    this.checkboxesGroupFieldKeys = this.agreements.map(agreement => agreement.code);
  }

  private handleAgreementsUpdateFromDifferentSection(): void {
    this.subscriptions.push(
      this.agreementsService.calculationAgreementSelectionUpdate$.subscribe(event => {
        const { code, selected } = event;
        if (isNotNullOrUndefined(this.getControl(code))) {
          this.getControl(code).patchValue(selected, {emitEvent: false});
          if (isNotNullOrUndefined(this.checkboxesGroup)) {
            this.checkboxesGroup.updateAllChecked();
          }
        }
      })
    );
  }
}
